/**
 * Tracking Helper for Google Ads, Meta (Facebook) Pixel, and TikTok Pixel
 *
 * This module provides helper functions for tracking conversions
 * for Glamping, Meteor, New Year, and Ramadan 2026 campaigns.
 *
 * UPDATE: Added dynamic TikTok pixel support for specific Ramadan pages.
 */

// Google Ads Conversion IDs
const GOOGLE_ADS_ID = 'AW-16843097853';

// Package-specific Google Ads conversion mappings
const PACKAGE_CONVERSIONS = {
  // Glamping Premium
  'Glamping-Mleiha---Premium-5737': `${GOOGLE_ADS_ID}/WyEpCKPts8wbEP2Ntd8-`,
  // Glamping Standard
  'Glamping-Mleiha----Standard-4611': `${GOOGLE_ADS_ID}/V5rqCLPFrcwbEP2Ntd8-`,
  'Mleiha-Glamping-Standard-Tent-December-31-2025-2654': `${GOOGLE_ADS_ID}/V5rqCLPFrcwbEP2Ntd8-`,
  // Meteor Premium (Overnight)
  'Geminids-Meteor-Shower-Overnight-Camping-Ticket-8397': `${GOOGLE_ADS_ID}/iqBHCPqGxMwbEP2Ntd8-`,
  // Meteor Standard
  'Mleiha-Geminids-Meteor-Shower-2025-5112': `${GOOGLE_ADS_ID}/u3ruCMbNvcwbEP2Ntd8-`,
  // New Year - no specific conversion, will use generic ViewContent only
  'New-Year-Under-the-Stars-Mleiha-National-Park-1979': null,
};

// Checkout conversion IDs
const CHECKOUT_CONVERSIONS = {
  checkout1: `${GOOGLE_ADS_ID}/8RrICKbts8wbEP2Ntd8-`, // AddToCart stage
  checkout2: `${GOOGLE_ADS_ID}/q0vgCKnts8wbEP2Ntd8-`, // Purchase stage
};

// Campaign-specific Purchase conversion IDs
const PURCHASE_CONVERSIONS = {
  // Glamping packages
  'Glamping-Mleiha---Premium-5737': `${GOOGLE_ADS_ID}/zhfwCNHMtdIbEP2Ntd8-`,
  'Glamping-Mleiha----Standard-4611': `${GOOGLE_ADS_ID}/zhfwCNHMtdIbEP2Ntd8-`,
  'Mleiha-Glamping-Standard-Tent-December-31-2025-2654': `${GOOGLE_ADS_ID}/zhfwCNHMtdIbEP2Ntd8-`,
  // New Year packages
  'New-Year-Under-the-Stars-Mleiha-National-Park-1979': `${GOOGLE_ADS_ID}/eDOdCNTMtdIbEP2Ntd8-`,
};

// Campaign listing page slugs (decoded)
const CAMPAIGN_LISTING_SLUGS = [
  'Glamping and Camping',
  'Mleiha Astronomy Events 2025',
  'Geminids Meteor Shower Event 2025',
  'RAMADAN 2026 – Iftar and Suhoor Under the Stars',
];

// Default TikTok Pixel ID (Global)
const DEFAULT_TIKTOK_PIXEL_ID = 'CVB0OABC77U0I1L5OCG0';

// Ramadan 2026 Pixel ID
const RAMADAN_TIKTOK_PIXEL_ID = 'D66O0AJC77UBN50SM8E0';

// Current active TikTok Pixel ID (stateful)
let currentTikTokPixelId = null;

// Map of package slugs/URLs to specific TikTok Pixels
const PAGE_SPECIFIC_TIKTOK_PIXELS = {
  // Ramadan 2026 packages
  'Ramadan-Star-Lounge-2634': RAMADAN_TIKTOK_PIXEL_ID,
  'Ramadan-Star-Lounge-Suhoor-7586': RAMADAN_TIKTOK_PIXEL_ID,
  'Under-the-Stars:-Mleiha-Overnight-Iftar-&-Suhoor-6301': RAMADAN_TIKTOK_PIXEL_ID,
  'Ramadan-Lounge-2634': RAMADAN_TIKTOK_PIXEL_ID,
  // Listing page slugs (Checking generic logic in init function)
};

// Ramadan 2026 — TikTok custom event mapping (package slug → event name)
const RAMADAN_PACKAGE_TIKTOK_EVENTS = {
  'Ramadan-Star-Lounge-2634': 'Ramadan Star Lounge-Iftar',
  'Ramadan-Star-Lounge-Suhoor-7586': 'Ramadan Star Lounge-Suhoor',
  'Under-the-Stars:-Mleiha-Overnight-Iftar-&-Suhoor-6301': 'Mleiha Overnight Iftar and Suhoor',
};

// Ramadan 2026 — Google Ads conversion IDs
const RAMADAN_CHECKOUT_CONVERSION = `${GOOGLE_ADS_ID}/cE21CJzUnvcbEP2Ntd8-`;
const RAMADAN_PURCHASE_CONVERSION = `${GOOGLE_ADS_ID}/6sgXCJ_UnvcbEP2Ntd8-`;

// Meta Pixel ID for campaigns
const META_PIXEL_ID = '556522438278059';

// Page-specific Meta Pixel configurations
const PAGE_SPECIFIC_PIXELS = {
  'Hike-with-Ajmal-6602': '568370940464269',
};

// Track which events have been fired to prevent duplicates
const firedEvents = new Set();

// Track which page-specific pixels have been initialized
const initializedPagePixels = new Set();

// Debug mode - set to false for production
const DEBUG_TRACKING = false;

/**
 * Debug logger for tracking events
 */
const debugLog = (source, eventType, data = {}) => {
  if (!DEBUG_TRACKING) return;
  
  const timestamp = new Date().toISOString();
  
  console.log(`%c[TRACKING DEBUG - ${source}]`, 'background: #ff6b6b; color: white; padding: 2px 6px; border-radius: 3px;', {
    timestamp,
    eventType,
    data,
    currentTikTokPixel: currentTikTokPixelId,
    url: window.location.href,
  });
};

/**
 * Check if gtag is available
 */
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Check if fbq (Meta Pixel) is available
 */
const isFbqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Check if TikTok Pixel (ttq) is available
 */
const isTtqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.ttq !== 'undefined';
};

/**
 * Initialize TikTok Pixel based on the current page/package
 * This should be called on route changes
 * @param {string} pathOrSlug - Current URL path or package slug
 */
export const initTikTokPixel = (pathOrSlug) => {
  if (!isTtqAvailable()) return;

  // Default to null - we only want to fire for specific Ramadan campaigns now
  let newPixelId = null;
  let decodedPath = '';
  
  try {
    decodedPath = decodeURIComponent(pathOrSlug || window.location.pathname);
  } catch (e) {
    decodedPath = pathOrSlug || '';
  }

  // Check if current path matches any specific pixel rules
  // 1. Direct Slug Match
  if (PAGE_SPECIFIC_TIKTOK_PIXELS[pathOrSlug]) {
    newPixelId = PAGE_SPECIFIC_TIKTOK_PIXELS[pathOrSlug];
  } 
  // 2. Path contains Ramadan slug key
  else if (Object.keys(PAGE_SPECIFIC_TIKTOK_PIXELS).some(slug => decodedPath.includes(slug))) {
    newPixelId = RAMADAN_TIKTOK_PIXEL_ID;
  }
  // 3. Fallback: Specific listing page check for Ramadan
  else if (decodedPath.includes('RAMADAN 2026') || decodedPath.includes('Ramadan-Star-Lounge')) {
    newPixelId = RAMADAN_TIKTOK_PIXEL_ID;
  }

  // If no pixel ID identified for this route, reset current ID and return
  if (!newPixelId) {
    debugLog('trackingHelper.js', 'TikTok Pixel SKIPPED (Not a Ramadan route)', { path: decodedPath });
    currentTikTokPixelId = null; // Reset current pixel ID to null so no events fire
    return;
  }

  if (currentTikTokPixelId !== newPixelId) {
    debugLog('trackingHelper.js', 'TikTok Pixel SWITCH', { from: currentTikTokPixelId, to: newPixelId, path: decodedPath });
  }

  currentTikTokPixelId = newPixelId;
  
  // Always load/ensure the current required pixel is active in the ttq stack
  window.ttq.load(newPixelId);
  window.ttq.page(); // Fire page view for this specific pixel
};

/**
 * Fire a TikTok Pixel event
 * @param {string} eventName - The event name (standard or custom)
 * @param {object} data - Event data (contents, value, currency, etc.)
 */
export const fireTikTokEvent = (eventName, data = {}) => {
  if (!currentTikTokPixelId) {
    debugLog('trackingHelper.js', `TikTok ${eventName} SKIPPED (No active pixel)`, data);
    return;
  }

  debugLog('trackingHelper.js', `TikTok ${eventName} ATTEMPT [${currentTikTokPixelId}]`, data);

  if (!isTtqAvailable()) {
    console.warn('%c[TRACKING] ttq not available', 'color: orange;');
    return;
  }

  // Use instance() to target the specific pixel valid for this page
  // ttq.instance(id) returns a handle to that specific pixel
  const instance = window.ttq.instance(currentTikTokPixelId);
  
  // Fallback to global if instance returns something unexpected, but usually it works if loaded.
  const target = instance || window.ttq;

  if (Object.keys(data).length > 0) {
    target.track(eventName, data);
  } else {
    target.track(eventName);
  }
  debugLog('trackingHelper.js', `TikTok ${eventName} FIRED`, data);
};

/**
 * Check if a package slug belongs to a Ramadan 2026 campaign package
 * @param {string} packageSlug - The package URL slug
 * @returns {boolean}
 */
export const isRamadanPackage = (packageSlug) => {
  if (!packageSlug) return false;
  return Object.keys(RAMADAN_PACKAGE_TIKTOK_EVENTS).includes(packageSlug) || 
         Object.keys(PAGE_SPECIFIC_TIKTOK_PIXELS).includes(packageSlug) ||
         packageSlug.includes('Ramadan');
         // Added broad check for robustness
};

/**
 * Fire TikTok custom ViewContent event for a Ramadan package
 * Each package fires its specific custom event name.
 * Uses deduplication to prevent multiple fires.
 * @param {string} packageSlug - The package URL slug
 * @param {string} packageName - The package display name
 * @param {string} packageId - The package ID
 */
export const trackRamadanPackageView = (packageSlug, packageName = '', packageId = '') => {
  // Ensure correct pixel is active first locally if checking here,
  // but initTikTokPixel should have handled it on route change.
  // Just in case, we can force update state if we know for sure.
  if (PAGE_SPECIFIC_TIKTOK_PIXELS[packageSlug]) {
     if (currentTikTokPixelId !== PAGE_SPECIFIC_TIKTOK_PIXELS[packageSlug]) {
         currentTikTokPixelId = PAGE_SPECIFIC_TIKTOK_PIXELS[packageSlug];
         // Note: We aren't calling load() here, assuming it was done on page load/route change.
         // If this is called w/o route change, we might miss the 'load'. 
         // But typical flow is Route -> Init -> Component Render -> trackRamadanPackageView
     }
  }

  const tiktokEventName = RAMADAN_PACKAGE_TIKTOK_EVENTS[packageSlug];
  if (!tiktokEventName) return;

  const eventKey = `ramadan_view_${packageSlug}`;
  if (firedEvents.has(eventKey)) return;
  firedEvents.add(eventKey);

  fireTikTokEvent(tiktokEventName, {
    contents: [
      {
        content_id: packageId || packageSlug,
        content_type: 'product',
        content_name: packageName || packageSlug,
      },
    ],
  });
};

/**
 * Fire a Google Ads conversion event
 * @param {string} sendTo - The conversion ID in format 'AW-XXXXX/XXXXX'
 * @param {string} transactionId - Optional transaction ID for deduplication
 */
export const fireGoogleConversion = (sendTo, transactionId = '') => {
  debugLog('trackingHelper.js', 'Google Ads Conversion ATTEMPT', { sendTo, transactionId });
  
  if (!isGtagAvailable()) {
    console.warn('%c[TRACKING] gtag not available', 'color: orange;');
    return;
  }

  const conversionData = {
    send_to: sendTo,
  };

  if (transactionId) {
    conversionData.transaction_id = transactionId;
  }

  window.gtag('event', 'conversion', conversionData);
  debugLog('trackingHelper.js', 'Google Ads Conversion FIRED', conversionData);
};

/**
 * Fire a Meta (Facebook) Pixel event
 * @param {string} eventName - The event name (e.g., 'ViewContent', 'AddToCart', 'Purchase')
 * @param {object} data - Optional event data
 */
export const fireMetaEvent = (eventName, data = {}) => {
  debugLog('trackingHelper.js', `Meta Pixel ${eventName} ATTEMPT`, data);
  
  if (!isFbqAvailable()) {
    console.warn('%c[TRACKING] fbq not available', 'color: orange;');
    return;
  }

  if (Object.keys(data).length > 0) {
    window.fbq('track', eventName, data);
  } else {
    window.fbq('track', eventName);
  }
  debugLog('trackingHelper.js', `Meta Pixel ${eventName} FIRED`, data);
};

/**
 * Get Google Ads conversion ID for a package slug
 * @param {string} packageSlug - The package URL slug
 * @returns {string|null} - The conversion ID or null if not a tracked package
 */
export const getPackageConversionId = (packageSlug) => {
  if (!packageSlug) return null;
  return PACKAGE_CONVERSIONS[packageSlug] || null;
};

/**
 * Check if a package slug is a tracked campaign package
 * @param {string} packageSlug - The package URL slug
 * @returns {boolean}
 */
export const isTrackedPackage = (packageSlug) => {
  if (!packageSlug) return false;
  return Object.keys(PACKAGE_CONVERSIONS).includes(packageSlug);
};

/**
 * Check if a listing page slug is a tracked campaign listing
 * @param {string} slug - The listing page slug (URL decoded)
 * @returns {boolean}
 */
export const isTrackedListingPage = (slug) => {
  if (!slug) return false;
  const decodedSlug = decodeURIComponent(slug);
  return CAMPAIGN_LISTING_SLUGS.includes(decodedSlug);
};

/**
 * Fire tracking events for package click/view
 * Only fires Google Ads conversion (Meta ViewContent is handled by existing ReactPixel code)
 * Uses deduplication to prevent multiple fires on the same page
 * @param {string} packageSlug - The package URL slug
 */
export const trackPackageClick = (packageSlug) => {
  if (!isTrackedPackage(packageSlug)) {
    return;
  }

  // Deduplicate: only fire once per package per page session
  const eventKey = `package_click_${packageSlug}`;
  if (firedEvents.has(eventKey)) {
    // console.log('Tracking already fired for:', packageSlug);
    return;
  }
  firedEvents.add(eventKey);

  // NOTE: Meta ViewContent is NOT fired here - it's handled by existing ReactPixel.track() in the components
  // We only fire Google Ads conversion for campaign packages
  const conversionId = getPackageConversionId(packageSlug);
  if (conversionId) {
    fireGoogleConversion(conversionId);
  }
};

/**
 * Fire tracking events for listing page view
 * Only fires for campaign listing pages - NO Meta event (to avoid duplicates with existing code)
 * Uses deduplication to prevent multiple fires
 * @param {string} slug - The listing page slug
 */
export const trackListingPageView = (slug) => {
  if (!isTrackedListingPage(slug)) {
    return;
  }

  // Deduplicate: only fire once per listing page per session
  const eventKey = `listing_view_${slug}`;
  if (firedEvents.has(eventKey)) {
    // console.log('Listing tracking already fired for:', slug);
    return;
  }
  firedEvents.add(eventKey);

  // NOTE: We don't fire Meta ViewContent here to avoid duplicates
  // The listing pages don't need separate Meta tracking - only package detail pages do
  // console.log('Listing page tracking noted for:', slug);
};

/**
 * Fire Checkout 1 tracking events (AddToCart stage)
 * Uses deduplication to prevent multiple fires
 * @param {string} packageSlug - Optional package slug for context
 */
export const trackCheckout1 = (packageSlug = '') => {
  // Deduplicate: only fire once per checkout session
  const eventKey = `checkout1_${packageSlug || 'generic'}`;
  if (firedEvents.has(eventKey)) {
    return;
  }
  firedEvents.add(eventKey);

  // Fire Meta AddToCart
  fireMetaEvent('AddToCart');

  // Fire Google Ads Checkout 1 conversion
  fireGoogleConversion(CHECKOUT_CONVERSIONS.checkout1);

  // Fire TikTok InitiateCheckout
  fireTikTokEvent('InitiateCheckout', {
    contents: [
      {
        content_id: packageSlug || 'unknown',
        content_type: 'product',
        content_name: packageSlug || 'unknown',
      },
    ],
  });

  // Fire Ramadan-specific Google Ads Begin Checkout conversion
  if (isRamadanPackage(packageSlug)) {
    fireGoogleConversion(RAMADAN_CHECKOUT_CONVERSION);
  }
};

/**
 * Fire Checkout 2 tracking events (Purchase stage)
 * @param {number} value - The order total value
 * @param {string} transactionId - The order/transaction ID for deduplication
 * @param {string} currency - Currency code (default: 'AED')
 */
export const trackCheckout2 = (value, transactionId, currency = 'AED', packageSlug = '') => {
  // Fire Meta Purchase with value
  fireMetaEvent('Purchase', {
    value: value,
    currency: currency,
  });

  // Fire Google Ads Checkout 2 conversion with transaction ID
  fireGoogleConversion(CHECKOUT_CONVERSIONS.checkout2, transactionId);

  // Fire TikTok Purchase
  fireTikTokEvent('Purchase', {
    contents: [
      {
        content_id: packageSlug || 'unknown',
        content_type: 'product',
        content_name: packageSlug || 'unknown',
      },
    ],
    value: value,
    currency: currency,
  });

  // Fire Ramadan-specific Google Ads Purchase conversion
  if (isRamadanPackage(packageSlug)) {
    fireGoogleConversion(RAMADAN_PURCHASE_CONVERSION, transactionId);
  }
};

/**
 * Generate a unique transaction ID using UUID v4 format
 * @returns {string}
 */
export const generateTransactionId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/**
 * Get Purchase conversion ID for a package slug (campaign-specific)
 * @param {string} packageSlug - The package URL slug
 * @returns {string|null} - The conversion ID or null if not a tracked package
 */
export const getPurchaseConversionId = (packageSlug) => {
  if (!packageSlug) return null;
  return PURCHASE_CONVERSIONS[packageSlug] || null;
};

/**
 * Fire campaign-specific Purchase conversion for Google Ads
 * @param {string} packageSlug - The package URL slug
 * @param {string} transactionId - The order/transaction ID for deduplication
 */
export const trackPurchaseConversion = (packageSlug, transactionId = '') => {
  const conversionId = getPurchaseConversionId(packageSlug);
  if (conversionId) {
    debugLog('trackingHelper.js', 'Campaign Purchase Conversion ATTEMPT', { packageSlug, conversionId, transactionId });
    fireGoogleConversion(conversionId, transactionId);
    // console.log('Campaign-specific Purchase conversion fired for:', packageSlug);
  }
};

/**
 * Get the page-specific Meta Pixel ID for a package slug
 * @param {string} packageSlug - The package URL slug
 * @returns {string|null} - The pixel ID or null if not configured
 */
export const getPageSpecificPixelId = (packageSlug) => {
  if (!packageSlug) return null;
  return PAGE_SPECIFIC_PIXELS[packageSlug] || null;
};

/**
 * Check if a package has a page-specific Meta Pixel
 * @param {string} packageSlug - The package URL slug
 * @returns {boolean}
 */
export const hasPageSpecificPixel = (packageSlug) => {
  if (!packageSlug) return false;
  return Object.keys(PAGE_SPECIFIC_PIXELS).includes(packageSlug);
};

/**
 * Initialize and fire page-specific Meta Pixel for a package
 * This creates a separate pixel instance for packages that have their own pixel ID
 * Uses deduplication to prevent multiple initializations
 * @param {string} packageSlug - The package URL slug
 */
export const initPageSpecificPixel = (packageSlug) => {
  if (!hasPageSpecificPixel(packageSlug)) {
    return;
  }

  const pixelId = getPageSpecificPixelId(packageSlug);
  if (!pixelId) return;

  // Deduplicate: only initialize once per page session
  const eventKey = `page_pixel_init_${packageSlug}`;
  if (initializedPagePixels.has(eventKey)) {
    debugLog('trackingHelper.js', 'Page-specific pixel already initialized', { packageSlug, pixelId });
    return;
  }
  initializedPagePixels.add(eventKey);

  debugLog('trackingHelper.js', 'Page-specific Meta Pixel INIT', { packageSlug, pixelId });

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Initialize the pixel via fbq so it can track future events
    if (typeof window.fbq === 'function') {
      window.fbq('init', pixelId);
      debugLog('trackingHelper.js', 'Page-specific Meta Pixel initialized via fbq', { packageSlug, pixelId });
    }
    
    // Use direct image pixel approach to ensure PageView fires ONLY for this specific pixel
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
    
    debugLog('trackingHelper.js', 'Page-specific Meta Pixel PageView FIRED via image', { packageSlug, pixelId });
  }
};

// Export constants for external use
export const TRACKING_CONFIG = {
  GOOGLE_ADS_ID,
  META_PIXEL_ID,
  DEFAULT_TIKTOK_PIXEL_ID,
  RAMADAN_TIKTOK_PIXEL_ID,
  PACKAGE_CONVERSIONS,
  CHECKOUT_CONVERSIONS,
  PURCHASE_CONVERSIONS,
  CAMPAIGN_LISTING_SLUGS,
  PAGE_SPECIFIC_PIXELS,
  RAMADAN_PACKAGE_TIKTOK_EVENTS,
};

const trackingHelpers = {
  initTikTokPixel,
  fireGoogleConversion,
  fireMetaEvent,
  fireTikTokEvent,
  getPackageConversionId,
  isTrackedPackage,
  isTrackedListingPage,
  isRamadanPackage,
  trackPackageClick,
  trackListingPageView,
  trackRamadanPackageView,
  trackCheckout1,
  trackCheckout2,
  generateTransactionId,
  getPurchaseConversionId,
  trackPurchaseConversion,
  getPageSpecificPixelId,
  hasPageSpecificPixel,
  initPageSpecificPixel,
  TRACKING_CONFIG,
};

export default trackingHelpers;
